<script setup lang="ts">
import { useHead } from '@unhead/vue'
import authV1BottomShape from '@images/svg/auth-v1-bottom-shape.svg?raw'
import authV1TopShape from '@images/svg/auth-v1-top-shape.svg?raw'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import AuthBrandHeader from '@/components/auth/AuthBrandHeader.vue'

const { t } = useI18n()

definePage({
  meta: {
    layout: 'blank',
    action: 'manage',
    subject: 'chat',
  },
})

const title = import.meta.env.VITE_APP_RU === 'only' ? 'base_title_ru' : 'base_title'

useHead({
  title: `${t(title)} - ${t('auth.verify_title')}`,
})

const formInProcess = ref<boolean>(false)
const btnClicked = ref<boolean>(false)
const tooManyRequests = ref<boolean>(false)

const router = useRouter()
const ability = useAbility()

const resendVerificationLink = async () => {
  try {
    const res = await $api('/auth/email/verification-notification', {
      method: 'POST',
      onResponseError({ response }) {
        if (response.status === 429) {
          tooManyRequests.value = true
          btnClicked.value = false
        }
      },
    })

    if (res.status === 'verification-link-sent') {
      btnClicked.value = true
      formInProcess.value = false
      tooManyRequests.value = false

      return
    }

    useCookie('userAbilityRules').value = res.data.userAbilityRules
    ability.update(res.data.userAbilityRules)

    useCookie('userData').value = res.data.userData

    await nextTick(() => {
      router.replace('/')
    })
  }
  catch (err) {
    formInProcess.value = false
    console.error(err)
  }
}

resendVerificationLink()
</script>

<template>
  <div class="auth-wrapper d-flex align-center justify-center pa-4">
    <div class="position-relative my-sm-16">
      <VNodeRenderer
        :nodes="h('div', { innerHTML: authV1TopShape })"
        class="text-primary auth-v1-top-shape d-none d-sm-block"
      />

      <VNodeRenderer
        :nodes="h('div', { innerHTML: authV1BottomShape })"
        class="text-primary auth-v1-bottom-shape d-none d-sm-block"
      />

      <VCard
        class="auth-card pa-4"
        max-width="448"
      >
        <VCardItem class="justify-center">
          <AuthBrandHeader />
        </VCardItem>

        <VCardText>
          <h4 class="text-h4 mb-1">
            {{ t('auth.verify_head') }}
          </h4>
          <p>{{ t('auth.verify_description') }}</p>

          <VAlert
            v-if="btnClicked"
            density="compact"
            color="primary"
            variant="tonal"
            class="mb-4"
          >
            {{ t('auth.verify_new_link') }}
          </VAlert>
          <VAlert
            v-if="tooManyRequests"
            density="compact"
            color="warning"
            variant="tonal"
            class="mb-4"
          >
            {{ t('auth.verify_spam') }}
          </VAlert>
        </VCardText>
      </VCard>
    </div>
  </div>
</template>

<style lang="scss">
@use "@core-scss/template/pages/page-auth.scss";
</style>
